import { useCallback, useEffect, useState } from "react";
import { Code2, Copy, Globe, KeyRound, Webhook, Zap } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const DELIVERY_STYLES = { delivered: "bg-green-50 text-green-700", pending: "bg-amber-50 text-amber-700", retrying: "bg-amber-50 text-amber-700", failed: "bg-red-50 text-red-700", cancelled: "bg-zinc-100 text-zinc-500" };
const AUTOMATION_ITEMS = [
  ["rtw_on_accept", "Request a Right to Work check when an offer is accepted"],
  ["identity_on_accept", "Request an Identity check when an offer is accepted"],
  ["references_on_accept", "Request References when an offer is accepted"],
];

function copy(text, label) {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied.`));
}

export default function EmployerIntegrations() {
  const [ov, setOv] = useState(null);
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [usage, setUsage] = useState([]);
  const [auto, setAuto] = useState({});
  const [keyForm, setKeyForm] = useState({ name: "", scopes: ["jobs:read"] });
  const [hookForm, setHookForm] = useState({ url: "", events: ["application.created"] });
  const [reveal, setReveal] = useState(null);
  const [saving, setSaving] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      api.get("/employer/integrations/overview").then((r) => { setOv(r.data); setAuto(r.data.automation || {}); }).catch(() => {}),
      api.get("/employer/integrations/keys").then((r) => setKeys(r.data)).catch(() => {}),
      api.get("/employer/integrations/webhooks").then((r) => setHooks(r.data)).catch(() => {}),
      api.get("/employer/integrations/deliveries").then((r) => setDeliveries(r.data)).catch(() => {}),
      api.get("/employer/integrations/usage").then((r) => setUsage(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    if (saving) return;
    setSaving("x");
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(""); }
  };

  const createKey = (e) => {
    e.preventDefault();
    act(async () => {
      const r = await api.post("/employer/integrations/keys", keyForm);
      setReveal({ kind: "key", value: r.data.key });
      setKeyForm({ name: "", scopes: ["jobs:read"] });
    }, null);
  };

  const createHook = (e) => {
    e.preventDefault();
    act(async () => {
      const r = await api.post("/employer/integrations/webhooks", hookForm);
      setReveal({ kind: "secret", value: r.data.secret });
      setHookForm({ url: "", events: ["application.created"] });
    }, null);
  };

  const toggle = (obj, setObj, field, val) => setObj({ ...obj, [field]: val });

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="int-loading">
        <div className="h-8 w-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="int-tab">
      <div>
        <h2 className="font-display text-2xl font-bold text-zinc-950">Integrations</h2>
        <p className="text-sm text-zinc-500 mt-1">Your careers page, website widget, API access, webhooks and workflow automation in one place.</p>
      </div>

      {reveal && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-4" data-testid="int-reveal">
          <p className="text-sm font-semibold text-zinc-900">Your {reveal.kind === "key" ? "API key" : "webhook signing secret"}, shown once, store it safely:</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-white border border-amber-200 rounded px-3 py-2 break-all" data-testid="int-reveal-value">{reveal.value}</code>
            <button onClick={() => copy(reveal.value, "Copied")} data-testid="int-reveal-copy"
              className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md bg-white hover:border-zinc-500 transition-colors">Copy</button>
            <button onClick={() => setReveal(null)} data-testid="int-reveal-dismiss"
              className="h-8 px-3 text-xs font-semibold text-zinc-500 hover:text-zinc-800 transition-colors">Done</button>
          </div>
        </div>
      )}

      <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="int-careers">
        <div className="flex items-center gap-2 mb-2">
          <Globe className="h-4 w-4 text-zinc-400" />
          <h3 className="font-semibold text-zinc-950">Careers page</h3>
        </div>
        <p className="text-sm text-zinc-500">Your branded page on HiredToday with all live roles. Set your brand colour and tagline in Settings, company profile.</p>
        {ov?.careers_url && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-zinc-50 border border-zinc-200 rounded px-3 py-2 break-all" data-testid="int-careers-url">{ov.careers_url}</code>
            <button onClick={() => copy(ov.careers_url, "Link")} data-testid="int-careers-copy"
              className="h-8 px-3 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Copy link</button>
            <a href={ov.careers_url} target="_blank" rel="noopener noreferrer" data-testid="int-careers-open"
              className="h-8 px-3 inline-flex items-center text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Open page</a>
          </div>
        )}
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="int-widget">
        <div className="flex items-center gap-2 mb-2">
          <Code2 className="h-4 w-4 text-zinc-400" />
          <h3 className="font-semibold text-zinc-950">Website widget</h3>
        </div>
        <p className="text-sm text-zinc-500">Embed your live roles on your own website. Jobs stay in sync automatically and applications arrive in your HiredToday dashboard.</p>
        {ov?.widget_snippet && (
          <>
            <pre className="mt-3 text-xs bg-zinc-950 text-zinc-100 rounded-md p-4 overflow-x-auto" data-testid="int-widget-snippet">{ov.widget_snippet}</pre>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <button onClick={() => copy(ov.widget_snippet, "Snippet")} data-testid="int-widget-copy"
                className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">
                <Copy className="h-3.5 w-3.5" /> Copy snippet
              </button>
              <span className="text-xs text-zinc-500" data-testid="int-widget-loads">{ov.widget_loads_7d} widget loads in the last 7 days</span>
            </div>
          </>
        )}
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="int-automation">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-4 w-4 text-zinc-400" />
          <h3 className="font-semibold text-zinc-950">Workflow automation</h3>
        </div>
        <p className="text-sm text-zinc-500">Applications already move interview to offer to hired automatically. Choose what else happens hands-free:</p>
        <div className="mt-3 space-y-2">
          {AUTOMATION_ITEMS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={!!auto[k]} disabled={!ov?.is_owner}
                onChange={(e) => toggle(auto, setAuto, k, e.target.checked)}
                data-testid={`int-auto-${k}`} className="h-4 w-4 accent-zinc-900" />
              {label}
            </label>
          ))}
        </div>
        {ov?.is_owner && (
          <button onClick={() => act(() => api.put("/employer/integrations/automation", auto), "Automation saved.")}
            data-testid="int-auto-save"
            className="mt-4 h-9 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors">Save automation</button>
        )}
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="int-keys">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-4 w-4 text-zinc-400" />
          <h3 className="font-semibold text-zinc-950">API keys</h3>
        </div>
        <p className="text-sm text-zinc-500">Read-only access to your jobs and applications over the API. Keys are stored hashed and shown once.</p>
        {ov?.is_owner && (
          <form onSubmit={createKey} className="mt-3 flex items-center gap-2 flex-wrap">
            <input value={keyForm.name} onChange={(e) => toggle(keyForm, setKeyForm, "name", e.target.value)}
              placeholder="Key name, e.g. Website sync" data-testid="int-key-name"
              className="h-9 px-3 border border-zinc-300 rounded-md text-sm w-56" />
            {(ov?.scopes || []).map((s) => (
              <label key={s} className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer">
                <input type="checkbox" checked={keyForm.scopes.includes(s)} data-testid={`int-key-scope-${s}`}
                  onChange={(e) => toggle(keyForm, setKeyForm, "scopes", e.target.checked ? [...keyForm.scopes, s] : keyForm.scopes.filter((x) => x !== s))}
                  className="h-3.5 w-3.5 accent-zinc-900" />
                {s}
              </label>
            ))}
            <button type="submit" disabled={saving === "x"} data-testid="int-key-create"
              className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors">Create key</button>
          </form>
        )}
        <div className="mt-3 space-y-2">
          {keys.length === 0 && <p className="text-xs text-zinc-400" data-testid="int-keys-empty">No API keys yet.</p>}
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-3 flex-wrap border border-zinc-100 rounded-md px-3 py-2" data-testid={`int-key-row-${k.id}`}>
              <code className="text-xs text-zinc-600">{k.prefix}…</code>
              <span className="text-xs text-zinc-500">{k.name} · {(k.scopes || []).join(", ")}</span>
              {k.last_used_at && <span className="text-xs text-zinc-400">last used {new Date(k.last_used_at).toLocaleDateString("en-GB")}</span>}
              {k.revoked ? (
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-100 text-zinc-500">revoked</span>
              ) : ov?.is_owner && (
                <button onClick={() => { if (window.confirm("Revoke this key? Integrations using it will stop working.")) act(() => api.delete(`/employer/integrations/keys/${k.id}`), "Key revoked."); }}
                  data-testid={`int-key-revoke-${k.id}`}
                  className="ml-auto h-7 px-2.5 text-xs font-semibold border border-zinc-300 rounded-md hover:border-red-400 hover:text-red-600 transition-colors">Revoke</button>
              )}
            </div>
          ))}
        </div>
        {usage.length > 0 && (
          <div className="mt-4" data-testid="int-usage">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Recent API usage</p>
            <div className="space-y-1">
              {usage.slice(0, 10).map((u, i) => (
                <p key={`usage-${i}`} className="text-xs text-zinc-500">
                  <span className={u.status === 200 ? "text-green-700" : "text-red-600"}>{u.status}</span> {u.endpoint} · {u.key_prefix}… · {new Date(u.at).toLocaleString("en-GB")}
                </p>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="int-webhooks">
        <div className="flex items-center gap-2 mb-2">
          <Webhook className="h-4 w-4 text-zinc-400" />
          <h3 className="font-semibold text-zinc-950">Webhooks</h3>
        </div>
        <p className="text-sm text-zinc-500">Get signed POST events (HMAC SHA-256, X-HiredToday-Signature header) as hiring moves. Failed deliveries retry twice, then show here.</p>
        {ov?.is_owner && (
          <form onSubmit={createHook} className="mt-3 space-y-2">
            <input value={hookForm.url} onChange={(e) => toggle(hookForm, setHookForm, "url", e.target.value)}
              placeholder="https://your-system.co.uk/hiredtoday-webhook" data-testid="int-hook-url"
              className="w-full h-9 px-3 border border-zinc-300 rounded-md text-sm" />
            <div className="flex items-center gap-3 flex-wrap">
              {(ov?.events || []).map((ev) => (
                <label key={ev} className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer">
                  <input type="checkbox" checked={hookForm.events.includes(ev)} data-testid={`int-hook-event-${ev}`}
                    onChange={(e) => toggle(hookForm, setHookForm, "events", e.target.checked ? [...hookForm.events, ev] : hookForm.events.filter((x) => x !== ev))}
                    className="h-3.5 w-3.5 accent-zinc-900" />
                  {ev}
                </label>
              ))}
            </div>
            <button type="submit" disabled={saving === "x"} data-testid="int-hook-create"
              className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors">Add webhook</button>
          </form>
        )}
        <div className="mt-3 space-y-2">
          {hooks.length === 0 && <p className="text-xs text-zinc-400" data-testid="int-hooks-empty">No webhooks yet.</p>}
          {hooks.map((w) => (
            <div key={w.id} className="flex items-center gap-3 flex-wrap border border-zinc-100 rounded-md px-3 py-2" data-testid={`int-hook-row-${w.id}`}>
              <span className="text-xs font-medium text-zinc-800 break-all">{w.url}</span>
              <span className="text-xs text-zinc-400">{(w.events || []).join(", ")}</span>
              {w.failed_deliveries > 0 && (
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-50 text-red-700" data-testid={`int-hook-fails-${w.id}`}>{w.failed_deliveries} failed</span>
              )}
              {ov?.is_owner && (
                <span className="ml-auto flex gap-2">
                  <button onClick={() => act(() => api.post(`/employer/integrations/webhooks/${w.id}/test`), "Test event queued.")}
                    data-testid={`int-hook-test-${w.id}`}
                    className="h-7 px-2.5 text-xs font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 transition-colors">Send test</button>
                  <button onClick={() => { if (window.confirm("Remove this webhook?")) act(() => api.delete(`/employer/integrations/webhooks/${w.id}`), "Webhook removed."); }}
                    data-testid={`int-hook-del-${w.id}`}
                    className="h-7 px-2.5 text-xs font-semibold border border-zinc-300 rounded-md hover:border-red-400 hover:text-red-600 transition-colors">Remove</button>
                </span>
              )}
            </div>
          ))}
        </div>
        {deliveries.length > 0 && (
          <div className="mt-4" data-testid="int-deliveries">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Recent deliveries</p>
            <div className="space-y-1.5">
              {deliveries.slice(0, 10).map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-xs text-zinc-500" data-testid={`int-delivery-${d.id}`}>
                  <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${DELIVERY_STYLES[d.status] || "bg-zinc-100 text-zinc-500"}`}>{d.status}</span>
                  <span className="font-medium text-zinc-700">{d.event}</span>
                  <span>{d.last_code ? `HTTP ${d.last_code}` : "no response"} · {d.attempts} {d.attempts === 1 ? "attempt" : "attempts"}</span>
                  <span className="ml-auto">{new Date(d.created_at).toLocaleString("en-GB")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
